import '../style.scss'

import React, {
    FC,
    FormEvent,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import LeftIcon from '~/assets/images/icons/arrow-left.svg?react'
import { SearchableInput } from '~/components/controls/Input/SearchableInput'
import { PageMeta } from '~/components/PageMeta'
import { SiteFooter } from '~/components/site-footer'
import { SiteHeader } from '~/components/site-header'
import { BreadcrumbStructuredData, CollectionPageStructuredData } from '~/components/StructuredData'
import { PageKey } from '~/constants/pageMeta'
import {
    AIRPORT_DIRECTORY_SEARCH_LIMIT,
    AIRPORT_DIRECTORY_SEARCH_MIN_LENGTH,
    formatAirportCount,
    getAirportCountryGroups,
    getAirportCountryMeta,
    getAirportCountryPath,
    getAirportDirectoryMeta,
    getAirportDirectoryPath,
    getAirportVisiblePageNumbers,
} from '~/helpers/airport-directory'
import {
    formatAirportLocation,
    formatAirportTitle,
    normalizeAirportText,
} from '~/helpers/airport-seo'
import { GOOGLE_EVENTS, sendGoogleGoal } from '~/libs/analytic/ga'
import type { AirportDirectoryAirport } from '~/types/airport-directory'

import {
    airportDirectoryCountriesSelector,
    airportDirectoryCountrySelector,
    airportDirectoryPageSelector,
    airportDirectoryTotalAirportsSelector,
    airportDirectoryTotalPagesSelector,
    clearSearchedAirports,
    defaultAirportsSelector,
    isLoadingSelector,
    searchAirports,
    searchedAirportsSelector,
    searchErrorSelector,
    searchQuerySelector,
    searchStatusSelector,
} from '../rtk/airportsSlice'
import type { AppDispatch } from '../rtk/store'

const SEARCH_DEBOUNCE_MS = 350
type AirportDirectoryListView = 'airports' | 'countries'

const getAirportCodes = (airport: AirportDirectoryAirport) => Array.from(new Set([
    airport?.iata_code,
    airport?.icao_code,
]
    .map(normalizeAirportText)
    .filter(Boolean)
    .map((code) => code.toUpperCase())))

const Page: FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const defaultAirports = useSelector(defaultAirportsSelector)
    const searchedAirports = useSelector(searchedAirportsSelector)
    const isLoading = useSelector(isLoadingSelector)
    const searchError = useSelector(searchErrorSelector)
    const searchQuery = useSelector(searchQuerySelector)
    const searchStatus = useSelector(searchStatusSelector)
    const currentPage = useSelector(airportDirectoryPageSelector)
    const totalAirports = useSelector(airportDirectoryTotalAirportsSelector)
    const totalPages = useSelector(airportDirectoryTotalPagesSelector)
    const countries = useSelector(airportDirectoryCountriesSelector)
    const country = useSelector(airportDirectoryCountrySelector)
    const [searchValue, setSearchValue] = useState('')
    const [listView, setListView] = useState<AirportDirectoryListView>('airports')
    const normalizedSearchValue = searchValue.trim()
    const isSearchActive = normalizedSearchValue.length >= AIRPORT_DIRECTORY_SEARCH_MIN_LENGTH
    const countryName = normalizeAirportText(country?.name)
    const countryGroups = useMemo(() => getAirportCountryGroups(countries), [countries])
    const pageMeta = country
        ? getAirportCountryMeta(country.name, country.slug, currentPage, totalPages)
        : getAirportDirectoryMeta(currentPage, totalPages)
    const canonicalUrl = `https://worldtransair.com${pageMeta.canonicalPath}`
    const pageNumbers = getAirportVisiblePageNumbers(currentPage, totalPages)
    const getPagePath = (pageNumber: number) => (
        country
            ? getAirportCountryPath(country.slug, pageNumber)
            : getAirportDirectoryPath(pageNumber)
    )
    const collectionItems = defaultAirports.map((airport) => ({
        name: formatAirportTitle(airport),
        url: `https://worldtransair.com/airport/${airport.icao_code.toLowerCase()}`,
    }))
    const isCurrentSearch = searchQuery.trim() === normalizedSearchValue
    const isSearching = isSearchActive && (
        isLoading
        || searchStatus === 'idle'
        || !isCurrentSearch
    )
    const hasSearchFailed = isSearchActive && isCurrentSearch && searchStatus === 'failed'
    const hasSearchCompleted = isSearchActive && isCurrentSearch && searchStatus === 'succeeded'
    const visibleAirports = isSearchActive ? searchedAirports : defaultAirports
    const searchTotal = hasSearchCompleted
        ? Number(searchedAirports[0]?.search_total_count || searchedAirports.length)
        : 0
    let pageHeading = 'Airports & Ground Handlers'
    let pageSubtitle = 'Find information about airports, runways, and ground handlers worldwide.'
    let resultsHeading = 'Browse all airports'

    if (country) {
        pageHeading = `Airports in ${country.name}${currentPage > 1 ? ` – Page ${currentPage}` : ''}`
        pageSubtitle = `Find information about airports, runways, and ground handlers in ${country.name}.`
        resultsHeading = `Airports in ${country.name}`
    } else if (currentPage > 1) {
        pageHeading = `Airport directory – Page ${currentPage}`
    }

    if (isSearchActive) resultsHeading = 'Search results'

    useEffect(() => {
        if (!isSearchActive) {
            dispatch(clearSearchedAirports())
            return undefined
        }

        let abortRequest: (() => void) | undefined
        const timeoutId = window.setTimeout(() => {
            const request = dispatch(searchAirports({
                query: normalizedSearchValue,
                country: countryName,
            }))
            abortRequest = request.abort

            request.unwrap()
                .then((payload) => {
                    const results = payload.airports || []
                    const total = Number(results[0]?.search_total_count || results.length)

                    sendGoogleGoal(GOOGLE_EVENTS.airport_directory_search, {
                        country_scope: countryName || 'all',
                        query_length: normalizedSearchValue.length,
                        result_count: total,
                    })
                })
                .catch(() => {})
        }, SEARCH_DEBOUNCE_MS)

        return () => {
            window.clearTimeout(timeoutId)
            abortRequest?.()
        }
    }, [countryName, dispatch, isSearchActive, normalizedSearchValue])

    const clearSearch = () => {
        setSearchValue('')
        dispatch(clearSearchedAirports())
    }

    const handleSearchChange = (value: string) => {
        setSearchValue(value)
        if (value.trim()) setListView('airports')
    }

    const handleListViewChange = (view: AirportDirectoryListView) => {
        if (view === listView) return

        if (view === 'countries') clearSearch()
        setListView(view)
        sendGoogleGoal(GOOGLE_EVENTS.airport_directory_countries_toggle, { view })
    }

    const handleSearchSubmit = (event: FormEvent) => {
        event.preventDefault()
        if (!hasSearchCompleted || !searchedAirports.length) return

        const exactQuery = normalizedSearchValue.toUpperCase()
        const exactAirport = searchedAirports.find((airport) => (
            getAirportCodes(airport).includes(exactQuery)
        ))

        if (exactAirport?.icao_code) {
            sendGoogleGoal(GOOGLE_EVENTS.airport_directory_result_click, {
                airport_icao: exactAirport.icao_code,
                source: 'exact_search_submit',
            })
            window.location.assign(`/airport/${exactAirport.icao_code.toLowerCase()}`)
            return
        }

        document.querySelector('#airport-results-title')?.scrollIntoView({ behavior: 'smooth' })
    }

    const getSearchMessage = () => {
        if (searchValue && !isSearchActive) {
            return `Enter at least ${AIRPORT_DIRECTORY_SEARCH_MIN_LENGTH} characters to search.`
        }
        if (isSearching) return 'Searching airports…'
        if (hasSearchFailed) return searchError
        if (hasSearchCompleted && searchTotal > AIRPORT_DIRECTORY_SEARCH_LIMIT) {
            return `Showing the first ${AIRPORT_DIRECTORY_SEARCH_LIMIT} of ${searchTotal.toLocaleString('en-US')} matches.`
        }
        if (hasSearchCompleted) return `${formatAirportCount(searchTotal)} found.`

        return countryName
            ? `Search by airport name, city, IATA, or ICAO code in ${countryName}.`
            : 'Search by airport name, city, country, IATA, or ICAO code.'
    }

    const trackAirportClick = (airport: AirportDirectoryAirport, index: number) => {
        sendGoogleGoal(GOOGLE_EVENTS.airport_directory_result_click, {
            airport_icao: airport.icao_code,
            position: index + 1,
            source: isSearchActive ? 'search' : 'directory',
        })
    }

    return (
        <>
            <PageMeta
                pageKey={PageKey.AIRPORTS}
                title={pageMeta.title}
                description={pageMeta.description}
                canonicalPath={pageMeta.canonicalPath}
            />
            <CollectionPageStructuredData
                name={pageMeta.title.replace(' | World Trans Air', '')}
                description={pageMeta.description}
                url={canonicalUrl}
                items={collectionItems}
            />
            {country && (
                <BreadcrumbStructuredData items={[
                    { name: 'World Trans Air', url: 'https://worldtransair.com/' },
                    { name: 'Airport directory', url: 'https://worldtransair.com/airports' },
                    { name: country.name, url: `https://worldtransair.com/airports/${country.slug}` },
                ]}
                />
            )}
            <div className="handlers grey-block">
                <SiteHeader />
                <main className="workspace-container">
                    {country && (
                        <a className="handlers__back" href="/airports">
                            <LeftIcon width="20" height="20" aria-hidden="true" />
                            Back
                        </a>
                    )}
                    <section className="handlers__hero" aria-labelledby="airport-directory-title">
                        <h1 id="airport-directory-title" className="handlers__title">
                            {pageHeading}
                        </h1>
                        <p className="handlers__subtitle">
                            {pageSubtitle}
                        </p>
                    </section>

                    <section className="handlers__finder" aria-labelledby="airport-finder-title">
                        <div className="handlers__finder-copy">
                            <h2 id="airport-finder-title">
                                {countryName ? `Find an airport in ${countryName}` : 'Find an airport'}
                            </h2>
                        </div>
                        <form className="handlers__search" role="search" onSubmit={handleSearchSubmit}>
                            <div className="handlers__search-controls">
                                <SearchableInput
                                    id="airport-directory-search"
                                    name="airport-search"
                                    value={searchValue}
                                    placeholder="Search"
                                    onChange={handleSearchChange}
                                    modClass="handlers-search"
                                    isLoading={isSearching}
                                    ariaLabel="Search airport directory"
                                    ariaDescribedBy="airport-search-status"
                                />
                                {searchValue && (
                                    <button
                                        type="button"
                                        className="handlers__search-clear"
                                        aria-label="Clear search"
                                        title="Clear search"
                                        onClick={clearSearch}
                                    >
                                        <span aria-hidden="true">×</span>
                                    </button>
                                )}
                            </div>
                            <div
                                id="airport-search-status"
                                className={`handlers__search-status ${hasSearchFailed ? 'handlers__search-status_error' : ''}`}
                                aria-live="polite"
                            >
                                {getSearchMessage()}
                            </div>
                        </form>
                    </section>

                    {!country && currentPage === 1 && (
                        <div className="handlers__view-switch" role="group" aria-label="Directory view">
                            <button
                                type="button"
                                aria-pressed={listView === 'airports'}
                                aria-controls="airport-directory-airports-panel"
                                className={`handlers__view-switch-button ${listView === 'airports' ? 'handlers__view-switch-button_active' : ''}`}
                                onClick={() => handleListViewChange('airports')}
                            >
                                Airports
                            </button>
                            <button
                                type="button"
                                aria-pressed={listView === 'countries'}
                                aria-controls="airport-directory-countries-panel"
                                className={`handlers__view-switch-button ${listView === 'countries' ? 'handlers__view-switch-button_active' : ''}`}
                                onClick={() => handleListViewChange('countries')}
                            >
                                Countries
                            </button>
                        </div>
                    )}

                    {!country && currentPage === 1 && countries.length > 0 && (
                        <section
                            id="airport-directory-countries-panel"
                            className="handlers__countries-panel"
                            aria-labelledby="airport-countries-title"
                            hidden={listView !== 'countries'}
                        >
                            <div className="handlers__results-heading">
                                <div>
                                    <h2 id="airport-countries-title">Browse by country</h2>
                                    <p>{`${countries.length.toLocaleString('en-US')} countries`}</p>
                                </div>
                            </div>
                            <div className="handlers__countries">
                                <div className="handlers__countries-content">
                                    <nav className="handlers__alphabet" aria-label="Country alphabet">
                                        {countryGroups.map((group) => (
                                            <a
                                                key={group.letter}
                                                className="handlers__alphabet-link"
                                                href={`#airport-countries-${group.letter}`}
                                            >
                                                {group.letter}
                                            </a>
                                        ))}
                                    </nav>
                                    <div className="handlers__country-groups">
                                        {countryGroups.map((group) => (
                                            <section
                                                key={group.letter}
                                                id={`airport-countries-${group.letter}`}
                                                className="handlers__country-group"
                                                aria-labelledby={`airport-countries-${group.letter}-title`}
                                            >
                                                <h3 id={`airport-countries-${group.letter}-title`}>{group.letter}</h3>
                                                <ul className="handlers__country-grid">
                                                    {group.countries.map((item) => (
                                                        <li key={item.slug}>
                                                            <a
                                                                className="handlers__country-link"
                                                                href={`/airports/${item.slug}`}
                                                                onClick={() => sendGoogleGoal(
                                                                    GOOGLE_EVENTS.airport_directory_country_click,
                                                                    { country: item.name },
                                                                )}
                                                            >
                                                                <span>{item.name}</span>
                                                                <small>{formatAirportCount(item.airportCount)}</small>
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </section>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    <section
                        id="airport-directory-airports-panel"
                        className="handlers__results"
                        aria-labelledby="airport-results-title"
                        hidden={!country && currentPage === 1 && listView === 'countries'}
                    >
                        <div className="handlers__results-heading">
                            <div>
                                <h2 id="airport-results-title">
                                    {resultsHeading}
                                </h2>
                                <p>
                                    {isSearchActive
                                        ? getSearchMessage()
                                        : formatAirportCount(totalAirports)}
                                </p>
                            </div>
                            {!isSearchActive && currentPage > 1 && totalPages > 1 && (
                                <span className="handlers__results-page">{`Page ${currentPage} of ${totalPages}`}</span>
                            )}
                        </div>

                        {isSearching && (
                            <div className="handlers__loading" aria-hidden="true">
                                {Array.from({ length: 4 }, (_value, index) => (
                                    <div key={index} className="handlers__loading-item" />
                                ))}
                            </div>
                        )}
                        {hasSearchFailed && (
                            <div className="handlers__empty handlers__empty_error">
                                <strong>We couldn’t complete the search.</strong>
                                <span>Clear the search and try again.</span>
                            </div>
                        )}
                        {hasSearchCompleted && visibleAirports.length === 0 && (
                            <div className="handlers__empty">
                                <strong>No airports found</strong>
                                <span>Check the code or try a different airport, city, or country.</span>
                                <button type="button" onClick={clearSearch}>Clear search</button>
                            </div>
                        )}
                        {(!isSearchActive || hasSearchCompleted) && visibleAirports.length > 0 && (
                            <div className="handlers__list">
                                {visibleAirports.map((airport, index) => {
                                    const airportName = normalizeAirportText(airport.name) || formatAirportTitle(airport)
                                    const airportLocation = formatAirportLocation(airport)
                                    const airportCodes = getAirportCodes(airport)

                                    return (
                                        <a
                                            key={airport.id}
                                            href={`/airport/${airport.icao_code.toLowerCase()}`}
                                            className="handlers__item"
                                            aria-label={`View ${formatAirportTitle(airport)}`}
                                            onClick={() => trackAirportClick(airport, index)}
                                        >
                                            <span className="handlers__item-main">
                                                <strong className="handlers__item-title">{airportName}</strong>
                                                {airportLocation && (
                                                    <span className="handlers__item-text">{airportLocation}</span>
                                                )}
                                            </span>
                                            <span className="handlers__item-side">
                                                <span className="handlers__item-codes" aria-label="Airport codes">
                                                    {airportCodes.map((code) => <span key={code}>{code}</span>)}
                                                </span>
                                                <span className="handlers__item-arrow" aria-hidden="true">→</span>
                                            </span>
                                        </a>
                                    )
                                })}
                            </div>
                        )}
                    </section>

                    {listView === 'airports' && !isSearchActive && totalPages > 1 && (
                        <nav className="handlers__pagination" aria-label="Airport directory pages">
                            {currentPage > 1 && (
                                <a
                                    className="handlers__pagination-link handlers__pagination-link_direction"
                                    href={getPagePath(currentPage - 1)}
                                    rel="prev"
                                >
                                    Previous
                                </a>
                            )}
                            <div className="handlers__pagination-pages">
                                {pageNumbers.map((pageNumber, index) => (
                                    <React.Fragment key={pageNumber}>
                                        {index > 0 && pageNumber - pageNumbers[index - 1] > 1 && (
                                            <span className="handlers__pagination-ellipsis" aria-hidden="true">…</span>
                                        )}
                                        <a
                                            href={getPagePath(pageNumber)}
                                            className={`handlers__pagination-link ${pageNumber === currentPage ? 'handlers__pagination-link_active' : ''}`}
                                            aria-current={pageNumber === currentPage ? 'page' : undefined}
                                        >
                                            {pageNumber}
                                        </a>
                                    </React.Fragment>
                                ))}
                            </div>
                            {currentPage < totalPages && (
                                <a
                                    className="handlers__pagination-link handlers__pagination-link_direction"
                                    href={getPagePath(currentPage + 1)}
                                    rel="next"
                                >
                                    Next
                                </a>
                            )}
                        </nav>
                    )}
                </main>
                <SiteFooter />
            </div>
        </>
    )
}

export default Page
